/**
 * Zeichenreihenfolge für Trackinglayer.
 *
 * @enum {int}
 */
gc.gis.layer.TrackingLayerDrawOrder = {
	SELECT:    10,
	SKETCH:    20
};

/**
 * @classdesc
 * Interface für einen Tracking-Layer.
 *
 * @abstract
 * @class
 * @extends gc.gis.GCWrappable
 * @memberof gc.gis.layer
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 *
 * @param {gc.gis.map.GCAbstractMap} map Karte, welche die Objekte des Tracking-Layers darstellen soll
 * @param {Object} [opt_options] optionale Optionen
 * @param {boolean} [opt_options.allowClearDrawingArea=false] Flag welches anzeigt, ob die Inhalte des Tracking-Layer
 *      durch die Funktion "Zeichenfläche leeren", verändert werden dürfen
 * @param {int} [opt_options.drawOrder] Zeichenreihenfolge des Trackinglayer in der Liste aller Trackinglayer
 * @param {gc.gis.action.GCAbstractAction} [opt_options.clearDrawingAreaAction] Spezielle Aktion die ausgeführt wird,
 *    wenn die Funktion "Zeichenfläche leeren", für den Layer ausgelöst wird (Voraussetzung: das Flag
 *    opt_options.allowClearDrawingArea wurde gesetzt
 */
gc.gis.layer.GCTrackingLayer = function(map, opt_options)
{
    gc.gis.GCWrappable.call(this);

    gc.gis.assert.isInstance(map, gc.gis.map.GCAbstractMap);

    /**
     * @type {gc.gis.map.GCAbstractMap}
     * @protected
     */
    this._map = map;
	/**
     * @type {boolean}
	 * @protected
	 */
	this._allowClearDrawingArea = opt_options.allowClearDrawingArea || false;
	/**
	 * @type {gc.gis.action.GCAbstractAction}
     * @protected
	 */
	this._clearDrawingAreaAction = opt_options.clearDrawingAreaAction;
};
gc.gis.layer.GCTrackingLayer.prototype = Object.create(gc.gis.GCWrappable.prototype);

/**
 * Liefert ein Flag welches anzeigt, ob die Inhalte des Tracking-Layer durch die Funktion "Zeichenfläche leeren",
 * verändert werden dürfen.
 *
 * @return {boolean}
 */
gc.gis.layer.GCTrackingLayer.prototype.allowClearDrawingArea = function ()
{
    return this._allowClearDrawingArea;
};

/**
 * Liefert eine spezielle Aktion die ausgeführt wird, wenn die Inahlte des Tracking-Layer durch die Funktion
 * "Zeichenfläche leeren", verändert werden sollen.
 *
 * @return {gc.gis.action.GCAbstractAction|undefined}
 */
gc.gis.layer.GCTrackingLayer.prototype.getClearDrawingAreaAction = function ()
{
	return this._clearDrawingAreaAction;
};

/**
 * Schaltet die Sichtbarkeit des Layers.
 *
 * @abstract
 * @method
 * @param {boolean} visible
 * @public
 */
gc.gis.layer.GCTrackingLayer.prototype.setVisible = gc.gis.abstractMethod;

/**
 * Gibt an, ob der Layer sichtbar geschaltet ist.
 *
 * @abstract
 * @method
 * @return {boolean}
 * @public
 */
gc.gis.layer.GCTrackingLayer.prototype.isVisible = gc.gis.abstractMethod;


/**
 * Setzt die Anzeige aller Vektorgeometrien auf der Karte zurück.
 *
 * @abstract
 * @method
 * @public
 */
gc.gis.layer.GCTrackingLayer.prototype.clear = gc.gis.abstractMethod;

/**
 * Setzt die Darstellungseigenschaften aller Vektorgeometrien.
 *
 * @abstract
 * @method
 * @param {gc.gis.style.GCAbstractStyle[]}
 * @public
 */
gc.gis.layer.GCTrackingLayer.prototype.setStyle = gc.gis.abstractMethod;

/**
 * Zeigt eine Vektorgeometrie auf der Karte an und liefert das korrespondierende Feature-Objekt zurück.
 * Die Geometrie wird dazu ggf. in die Projektion des Tracking-Layers transformiert. Ggf. muss vorab ein Klone erzeugt
 * werden, sofern die ursprünglichen Koordinaten weiter benötigt werden.
 *
 * @abstract
 * @method
 * @param {gc.gis.geom.GCGeometry} geometry Geometrie
 * @return {gc.gis.feature.GCFeature}
 * @public
 */
gc.gis.layer.GCTrackingLayer.prototype.addGeometry = gc.gis.abstractMethod;

/**
 * Zeigt eine Liste von Vektorgeometrien auf der Karte an und liefert die korrespondierenden Feature-Objekte zurück.
 * Die Geometrien werden dazu ggf. in die Projektion des Tracking-Layers transformiert. Ggf. muss vorab ein Klone
 * erzeugt werden, sofern die ursprünglichen Koordinaten weiter benötigt werden.
 *
 * @abstract
 * @method
 * @param {gc.gis.geom.GCGeometry[]} geometries Liste der Geometrien
 * @return {gc.gis.feature.GCFeature[]}
 * @public
 */
gc.gis.layer.GCTrackingLayer.prototype.addGeometries = gc.gis.abstractMethod;

/**
 * Liefert die Liste der Features des Tracking-Layers.
 *
 * @abstract
 * @method
 * @return {gc.gis.feature.GCFeature[]}
 * @public
 */
gc.gis.layer.GCTrackingLayer.prototype.getFeatures = gc.gis.abstractMethod;

/**
 * Liefert ein Feature des Tracking-Layers anhand seiner ID oder <code>null</code>, sofern kein passendes Feature
 * existiert.
 *
 * @abstract
 * @method
 * @param {number | string} id
 * @return {gc.gis.feature.GCFeature|null}
 * @public
 */
gc.gis.layer.GCTrackingLayer.prototype.getFeatureByID = gc.gis.abstractMethod;

/**
 * Fügt ein Feature dem Trackinglayer hinzu. Dabei wird ggf. die Geometrie in das zur Karte passende CRS transformiert.
 *
 * @abstract
 * @method
 * @param {gc.gis.feature.GCFeature} feature Feature
 * @public
 */
gc.gis.layer.GCTrackingLayer.prototype.addFeature = gc.gis.abstractMethod;

/**
 * Fügt ein Liste von Features dem Trackinglayer hinzu. Dabei werden ggf. die Geometrien in das zur Karte passende CRS
 * transformiert.
 *
 * @abstract
 * @method
 * @param {gc.gis.feature.GCFeature[]} features Liste von Features
 * @public
 */
gc.gis.layer.GCTrackingLayer.prototype.addFeatures = gc.gis.abstractMethod;

/**
 * Löscht ein einzelnen Feature vom Trackinglayer.
 *
 * @abstract
 * @method
 * @param {gc.gis.feature.GCFeature} feature zu löschendes Feature
 * @public
 */
gc.gis.layer.GCTrackingLayer.prototype.removeFeature = gc.gis.abstractMethod;

/**
 * Löscht ein Menge von Features vom Trackinglayer.
 *
 * @abstract
 * @method
 * @param {gc.gis.feature.GCFeature[]} features Liste zu löschender Features
 * @public
 */
gc.gis.layer.GCTrackingLayer.prototype.removeFeatures = gc.gis.abstractMethod;

/**
 * Löscht ein Feature anhand seiner ID vom Trackinglayer.
 *
 * @abstract
 * @method
 * @param {number | string} id
 * @return {bool}
 * @public
 */
gc.gis.layer.GCTrackingLayer.prototype.removeFeatureByID = gc.gis.abstractMethod;

/**
 * Liefert die Anzahl der Features des Trackinglayers.
 *
 * @abstract
 * @method
 * @return {int} Anzahl der Features des Layers
 * @public
 */
gc.gis.layer.GCTrackingLayer.prototype.numFeatures = gc.gis.abstractMethod;